
import React, { useRef, useEffect } from 'react';
import { getBestAssetUrl } from './services/assets';
import { useAsset } from './hooks/useAsset';
import { Card, CardColor, CardType, Player, Tower } from './types';
import { Language, t, parseLog } from './data/translations';
import AnimatedCounter from './components/AnimatedCounter';
import { ElementWaveSignal } from './components/WaveSignal';
import { useShellLayout } from './hooks/useShellLayout';

export const INITIAL_HEALTH = 15;
export const HAND_SIZE = 99;
export const MAX_PREPARED_CARDS = 5;
export const ABSOLUTE_MAX_PREPARED_CARDS = 12;

export const DECK_COMPOSITION: Omit<Card, 'id'>[] = [
  // Red (Combat) - 8 cards
  ...Array(4).fill({ type: CardType.Attack, value: 1, typeB: CardType.Pushback, valueB: 1, color: CardColor.Red }),
  ...Array(2).fill({ type: CardType.Attack, value: 1, typeB: CardType.Pushback, valueB: 1, color: CardColor.Red }),
  ...Array(2).fill({ type: CardType.Attack, value: 1, typeB: CardType.Pushback, valueB: 1, color: CardColor.Red }),

  // Green (Heal/Disease) - 8 cards
  ...Array(8).fill({ type: CardType.Heal, value: 1, typeB: CardType.Disease, valueB: 1, color: CardColor.Green }),

  // Black (Draw) - 8 cards
  ...Array(8).fill({ type: CardType.PsySearch, value: 2, typeB: CardType.PsyBlock, valueB: 1, color: CardColor.Black }),

  // Purple (Steal) - 8 cards
  ...Array(8).fill({ type: CardType.Steal, value: 1, typeB: CardType.DoubleCross, valueB: 1, color: CardColor.Purple }),

  // Yellow (Utility) - 8 cards
  ...Array(8).fill({ type: CardType.Energize, value: 2, typeB: CardType.Shock, valueB: 2, color: CardColor.Yellow }),

  // Pink (Hibernation) - 8 cards
  ...Array(8).fill({ type: CardType.Hibernation, value: 1, typeB: CardType.Dispel, valueB: 1, color: CardColor.Pink }),
];

export const STASH_DECK_COMPOSITION: Omit<Card, 'id' | 'color'>[] = [
  // Armory
  ...Array(1).fill({ type: CardType.Attack, value: 5, essenceCost: 6, subType: 'Armory', faceSlug: 'ancient-sword' }), // Ancient Sword
  ...Array(1).fill({ type: CardType.Chromabow, value: 4, essenceCost: 6, subType: 'Armory', faceSlug: 'chromabow' }),
  ...Array(1).fill({ type: CardType.Defense, value: 5, essenceCost: 6, subType: 'Armory', extendedShield: true, faceSlug: 'ancient-shield' }), // Ancient Shield
  ...Array(1).fill({ type: CardType.Defense, value: 2, essenceCost: 5, subType: 'Armory', extendedShield: true, faceSlug: 'shimmering-shield' }), // Shimmering Shield
  ...Array(1).fill({ type: CardType.Defense, value: 2, essenceCost: 2, subType: 'Armory', extendedShield: true, faceSlug: 'steel-shield' }), // Steel Shield
  ...Array(1).fill({ type: CardType.Defense, value: 0, essenceCost: 6, subType: 'Armory', extendedShield: true, faceSlug: 'spikes' }), // Spikes
  ...Array(1).fill({ type: CardType.EnergySword, value: 2, essenceCost: 4, subType: 'Armory', faceSlug: 'energy-sword' }),
  ...Array(1).fill({ type: CardType.FlyingScythe, value: 2, essenceCost: 6, subType: 'Armory', faceSlug: 'flying-scythe' }),
  ...Array(1).fill({ type: CardType.InfernoGauntlet, value: 4, essenceCost: 6, subType: 'Armory', faceSlug: 'inferno-gauntlet' }),
  ...Array(1).fill({ type: CardType.Larcendagger, value: 3, essenceCost: 6, subType: 'Armory', faceSlug: 'larcendagger' }),
  ...Array(1).fill({ type: CardType.Attack, value: 3, essenceCost: 4, subType: 'Armory', faceSlug: 'the-sting' }), // The Sting
  ...Array(1).fill({ type: CardType.Attack, value: 8, essenceCost: 10, subType: 'Armory', faceSlug: 'sword-of-revenge' }), // Sword of Revenge
  ...Array(1).fill({ type: CardType.Attack, value: 3, essenceCost: 5, subType: 'Armory', playableAfterPass: true, faceSlug: 'paladins-hammer' }), // Paladin's Hammer
  // Spells
  ...Array(1).fill({ type: CardType.Attack, value: 2, essenceCost: 2, subType: 'Spell', faceSlug: 'fireball' }),
  ...Array(1).fill({ type: CardType.StealTower, value: 1, essenceCost: 5, subType: 'Spell', faceSlug: 'purification' }),
  ...Array(1).fill({ type: CardType.PsySearch, value: 3, essenceCost: 3, subType: 'Spell', faceSlug: 'card-cyclone' }),
  ...Array(1).fill({ type: CardType.LifeDrain, value: 2, essenceCost: 4, subType: 'Spell', faceSlug: 'life-drain' }),
  ...Array(1).fill({ type: CardType.Counterblast, value: 1, essenceCost: 3, subType: 'Spell', extendedShield: true, faceSlug: 'counterblast' }),
  ...Array(1).fill({ type: CardType.EyesOfMadness, value: 1, essenceCost: 5, subType: 'Spell', faceSlug: 'eyes-of-madness' }),
  ...Array(1).fill({ type: CardType.Frost, value: 1, essenceCost: 3, subType: 'Spell', faceSlug: 'frost' }),
  ...Array(1).fill({ type: CardType.Invincible, value: 1, essenceCost: 3, subType: 'Spell', extendedShield: true, faceSlug: 'invincible' }),
  ...Array(1).fill({ type: CardType.NaturalGrip, value: 2, essenceCost: 4, subType: 'Spell', faceSlug: 'natural-grip' }),
  ...Array(1).fill({ type: CardType.Steal, value: 1, essenceCost: 3, subType: 'Spell', extendedShield: true, playableAfterPass: true, faceSlug: 'shadow-burglary' }),
  ...Array(1).fill({ type: CardType.TwilightShift, value: 0, essenceCost: 4, subType: 'Spell', faceSlug: 'twilight-shift' }),
  ...Array(1).fill({ type: CardType.ThunderSprint, value: 5, essenceCost: 4, subType: 'Spell', faceSlug: 'thunder-sprint' }),
  ...Array(1).fill({ type: CardType.Trembling, value: 0, essenceCost: 7, subType: 'Spell', faceSlug: 'trembling' }),
  // Artifacts
  ...Array(1).fill({ type: CardType.PlantedBomb, value: 5, essenceCost: 8, subType: 'Artifact', extendedShield: true, faceSlug: 'planted-bomb' }),
  ...Array(1).fill({ type: CardType.Protector, value: 2, essenceCost: 4, subType: 'Artifact', extendedShield: true, playableAfterPass: true, faceSlug: 'protector' }),
  ...Array(1).fill({ type: CardType.BodyUpgrade, value: 5, essenceCost: 8, subType: 'Artifact', faceSlug: 'body-upgrade' }),
  ...Array(1).fill({ type: CardType.FortunesFavor, value: 1, essenceCost: 4, subType: 'Artifact', faceSlug: 'fortunes-favor' }),
  ...Array(1).fill({ type: CardType.ArcaneBoots, value: 2, essenceCost: 2, subType: 'Artifact', faceSlug: 'arcane-boots' }),
  ...Array(1).fill({ type: CardType.DiscerningEye, value: 1, essenceCost: 4, subType: 'Artifact', faceSlug: 'discerning-eye' }),
  ...Array(1).fill({ type: CardType.BrokenSecret, value: 1, essenceCost: 4, subType: 'Artifact', faceSlug: 'broken-secret' }),
  ...Array(1).fill({ type: CardType.CursedIron, value: 1, essenceCost: 6, subType: 'Artifact', faceSlug: 'cursed-iron' }),
  ...Array(1).fill({ type: CardType.Ale, value: 4, essenceCost: 6, subType: 'Artifact', faceSlug: 'ale' }),
  ...Array(1).fill({ type: CardType.LifeCharger, value: 1, essenceCost: 16, subType: 'Artifact', extendedShield: true, faceSlug: 'life-charger' }),
  ...Array(1).fill({ type: CardType.MadnessControl, value: 4, essenceCost: 14, subType: 'Artifact', faceSlug: 'madness-control' }),
  ...Array(1).fill({ type: CardType.SealOfSilence, value: 0, essenceCost: 6, subType: 'Artifact', extendedShield: true, playableAfterPass: true, faceSlug: 'seal-of-silence' }),
  ...Array(1).fill({ type: CardType.VoodooDoll, value: 1, essenceCost: 6, subType: 'Artifact', faceSlug: 'voodoo-doll' }),
];

export const TowerIcon: React.FC<{ className?: string, color?: string }> = ({ className, color }) => (
  <svg xmlns="http://www.w3.org/2000/svg" className={className} style={{ color: color }} viewBox="0 0 24 24" fill="currentColor">
    <path d="M10 2v3h4V2h-4zM6 7v3h3V7H6zm5 0v3h3V7h-3zm5 0v3h3V7h-3zM6 12v3h3v-3H6zm5 0v3h3v-3h-3zm5 0v3h3v-3h-3zM3 17v3h18v-3H3z" />
  </svg>
);

export const TowerCrystal: React.FC<{ color: CardColor | 'gray'; className?: string }> = ({ color, className = 'w-10 h-10' }) => {
  const src = useAsset(`ui.classic.${color === 'gray' ? 'grayCrystal' : color === CardColor.Red ? 'redCrystal' : color === CardColor.Pink ? 'pinkCrystal' : color === CardColor.Green ? 'greenCrystal' : color === CardColor.Black ? 'blackCrystal' : color === CardColor.Purple ? 'purpleCrystal' : 'yellowCrystal'}`);
  return <img src={src} alt={`${color} crystal`} className={`${className} object-contain drop-shadow-lg`} />;
};


export const ColorDecksDisplay: React.FC<{ colorDecks: Record<CardColor, Card[]>, availableTowers?: CardColor[], isDealing?: boolean, language?: Language }> = ({ colorDecks, availableTowers, isDealing = false, language = 'en' }) => {
  const colors = [CardColor.Red, CardColor.Pink, CardColor.Green, CardColor.Black, CardColor.Purple, CardColor.Yellow];
  const colorMap: Record<CardColor, string> = {
    [CardColor.Red]: 'bg-gradient-to-br from-red-400 to-red-600',
    [CardColor.Pink]: 'bg-gradient-to-br from-pink-400 to-pink-600',
    [CardColor.Green]: 'bg-gradient-to-br from-green-400 to-green-600',
    [CardColor.Black]: 'bg-black',
    [CardColor.Purple]: 'bg-gradient-to-br from-purple-400 to-purple-600',
    [CardColor.Yellow]: 'bg-gradient-to-br from-yellow-400 to-yellow-600',
  };
  const colorHexMap: Record<CardColor, string> = {
    [CardColor.Red]: '#ef4444',
    [CardColor.Pink]: '#ec4899',
    [CardColor.Green]: '#22c55e',
    [CardColor.Black]: '#000000',
    [CardColor.Purple]: '#a855f7',
    [CardColor.Yellow]: '#eab308',
  };
  const colorSlugs: Record<CardColor, string> = {
    [CardColor.Red]: 'red',
    [CardColor.Pink]: 'pink',
    [CardColor.Green]: 'green',
    [CardColor.Black]: 'black',
    [CardColor.Purple]: 'purple',
    [CardColor.Yellow]: 'yellow',
  };

  return (
    <div className="flex flex-col items-start gap-2 bg-black/30 p-2 rounded-lg">
      {colors.map(color => (
        <div key={color} className="flex items-center gap-3 w-full justify-between">
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 flex items-center justify-center">
              {(!availableTowers || availableTowers.includes(color)) && <TowerIcon className="w-5 h-5" color={colorHexMap[color]} />}
            </div>
            <img src={getBestAssetUrl(`cards.color.${colorSlugs[color]}.faceA`)} alt={t(`term.${colorSlugs[color]}`, language)} className="w-5 h-5 rounded object-contain" style={{ transform: isDealing ? 'translateX(6px)' : 'none', transition: 'transform 300ms' }} />
            <span className="font-semibold text-white text-sm w-14 text-left">{t(`term.${colorSlugs[color]}`, language)}</span>
          </div>
          <span className="font-bold text-white text-lg">
            {colorDecks[color]?.length || 0}
          </span>
        </div>
      ))}
    </div>
  );
};

export const StashDeckDisplay: React.FC<{ count: number }> = ({ count }) => {
  const cardBackUrl = useAsset('cards.back');
  return (
  <div>
    <div className="relative w-24 h-32 group">
      {Array(Math.min(count, 20)).fill(0).map((_, i) => (
        <img key={i} src={cardBackUrl} alt="stash" className="absolute w-20 h-[7.7rem] rounded-lg border-2 border-amber-400 shadow-lg object-cover" style={{ top: i * 1.5, left: i * 1.5 }} />
      ))}
      {count > 0 && (
        <div className="absolute -top-2 -left-3 w-8 h-8 rounded-full bg-slate-900 flex items-center justify-center font-bold text-white border-2 border-white opacity-0 group-hover:opacity-100 transition-opacity duration-300">
          {count}
        </div>
      )}
    </div>
  </div>
);
};

export const DealingDeckDisplay: React.FC<{ count: number }> = ({ count }) => {
  const cardBackUrl = useAsset('cards.back');
  return (
  <div className="relative w-24 h-32 group">
    {Array(Math.min(count, 10)).fill(0).map((_, i) => (
      <img key={i} src={cardBackUrl} alt="deck" className="absolute w-20 h-[7.7rem] rounded-lg border-2 border-slate-500 shadow-lg object-cover" style={{ top: i * 2, left: i * 2 }} />
    ))}
    {count > 0 && <div className="absolute -top-2 -left-3 w-8 h-8 rounded-full bg-slate-900 flex items-center justify-center font-bold text-white border-2 border-white opacity-0 group-hover:opacity-100 transition-opacity duration-300">
      {count}
    </div>}
  </div>
);
};

export const ShieldIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg xmlns="http://www.w3.org/2000/svg" className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 2L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-3z" />
  </svg>
);

export const HealthIcon: React.FC<{ className?: string }> = ({ className }) => {
  const src = useAsset('ui.hud.heart');
  return <img src={src} alt="health" className={className} />;
};

export const EssenceIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg xmlns="http://www.w3.org/2000/svg" className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 2L2 8.5l10 13.5L22 8.5L12 2zm0 2.34L19.66 8.5 12 18.33 4.34 8.5L12 4.34z" />
  </svg>
);


export const AttackIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg xmlns="http://www.w3.org/2000/svg" className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M14.803 2.003l-1.414 1.414L15.586 5.58l-1.47 1.47-1.47-1.47-1.414 1.414 1.47 1.47-1.47 1.47-1.414 1.414 1.47 1.47-1.47 1.47L9.5 17H5v-4.5l6.536-6.536 1.414-1.414 1.353-1.354zm-2.828 2.828l3.535 3.535-1.414 1.414-3.535-3.535 1.414-1.414zM2 18h11v2H2v-2z" />
  </svg>
);

export const DefenseIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg xmlns="http://www.w3.org/2000/svg" className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 2L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-3zm0 10.99h7c-.53 4.12-3.28 7.79-7 8.94V13H5V6.3l7-3.11v9.8z" />
  </svg>
);

export const PushbackIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg xmlns="http://www.w3.org/2000/svg" className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M13.71 18.71l-5-5a1 1 0 010-1.42l5-5a1 1 0 011.42 1.42L11.41 12l3.72 3.72a1 1 0 010 1.42 1 1 0 01-1.42 0zM18 6a1 1 0 00-1 1v8a1 1 0 002 0V7a1 1 0 00-1-1z" />
  </svg>
);

export const PsyBlockIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg xmlns="http://www.w3.org/2000/svg" className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 2C9.19 2 6.62 3.41 5 5.41V4a1 1 0 00-2 0v4a1 1 0 001 1h4a1 1 0 000-2H6.26A8.003 8.003 0 0112 4c4.41 0 8 3.59 8 8 0 1.84-.63 3.54-1.69 4.89a1 1 0 001.38 1.44C20.89 16.54 22 14.41 22 12c0-5.52-4.48-10-10-10zM5.31 17.11A1 1 0 004 18.55C5.11 20.21 7.03 21.5 9.25 21.91l-1.46-1.46a3.996 3.996 0 010-5.66l-1.41-1.41a6 6 0 000 8.49z" />
    <path d="M14 10.05c0-1.65-1.35-3-3-3s-3 1.35-3 3c0 .8.32 1.52.83 2.05L7.41 13.5a6 6 0 008.49 0l-1.41-1.41c.51-.53.83-1.25.83-2.04zM12 12c-.55 0-1-.45-1-1s.45-1 1-1 1 .45 1 1-.45 1-1 1z" />
  </svg>
);

export const PadlockIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg xmlns="http://www.w3.org/2000/svg" className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M18 8h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zM9 6c0-1.66 1.34-3 3-3s3 1.34 3 3v2H9V6zm9 14H6V10h12v10zm-6-3c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2z" />
  </svg>
);


export const HealIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg xmlns="http://www.w3.org/2000/svg" className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M19 12h-6V6h-2v6H5v2h6v6h2v-6h6v-2z" />
  </svg>
);

export const DrawIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg xmlns="http://www.w3.org/2000/svg" className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M4 6h16v2H4zm0 5h16v2H4zm0 5h16v2H4z" />
  </svg>
);

export const StealIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg xmlns="http://www.w3.org/2000/svg" className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M18.88 14.18C18.29 13.59 17.5 13 16 13s-2.29.59-2.88 1.18c-.2.19-.51.19-.71 0l-.71-.71a.5.5 0 00-.71 0C10.4 14.09 10 15.05 10 16c0 .34.03.68.08 1H4v-5h3V9H4V6h5.73C9.28 5.4 9 4.74 9 4c0-1.1.9-2 2-2s2 .9 2 2c0 .74-.28 1.4-.73 1.9L16 9.5V11h3v3.08c.32-.05.66-.08 1-.08.95 0 1.91.4 2.5 1 .49.49.49 1.28 0 1.77l-.71.71c-.2.2-.51.2-.71 0z" />
  </svg>
);

export const getCardDisplayName = (type: CardType, value?: number, subType?: string, faceSlug?: string, lang: Language = 'en'): string => {
  if (subType === 'Armory' || subType === 'Artifact' || subType === 'Spell') {
    if (faceSlug) {
      const key = `card.${faceSlug}.name`;
      const translated = t(key, lang);
      if (translated !== key) return translated;
    }
  }

  // Fallback map for special types if faceSlug translation missing
  const typeMap: Partial<Record<CardType, string>> = {
    [CardType.Attack]: 'term.attack',
    [CardType.Pushback]: 'term.pushback',
    [CardType.Defense]: 'term.defense',
    [CardType.Heal]: 'term.heal',
    [CardType.Disease]: 'term.disease',
    [CardType.PsySearch]: 'term.psy-search',
    [CardType.PsyBlock]: 'term.psy-block',
    [CardType.Steal]: 'term.steal',
    [CardType.DoubleCross]: 'term.double-cross',
    [CardType.Shock]: 'term.shock',
    [CardType.Energize]: 'term.energize',
    [CardType.Hibernation]: 'term.hibernation',
    [CardType.Dispel]: 'term.dispel',
  };

  if (typeMap[type]) {
    return t(typeMap[type]!, lang);
  }

  return String(type).split('_').map(s => s.charAt(0).toUpperCase() + s.slice(1).toLowerCase()).join(' ');
};

export const getCardDisplayNameForCard = (card: Card, rotation: number = 0, lang: Language = 'en'): string => {
  const norm = (rotation % 360 + 360) % 360;
  const flipped = norm > 90 && norm < 270;
  const t = flipped && card.typeB ? card.typeB : card.type;
  const v = flipped && card.valueB ? card.valueB : card.value;
  return getCardDisplayName(t, v, card.subType, (card as any).faceSlug, lang);
};

export const getCardSummaryForType = (type: CardType, faceSlug?: string, lang: Language = 'en'): string => {
  if (faceSlug) {
    const key = `card.${faceSlug}.desc`;
    const translated = t(key, lang);
    if (translated !== key) return translated;
  }

  const key = `card.${type.toLowerCase().replace(/_/g, '-')}.desc`;
  const translated = t(key, lang);
  if (translated !== key) return translated;

  return t('ui.specialEffect', lang);
}

export const getTypeIconForType = (t: CardType, cls: string = 'w-5 h-5'): React.ReactNode => {
  switch (t) {
    case CardType.Attack: return <AttackIcon className={cls} />
    case CardType.Defense: return <DefenseIcon className={cls} />
    case CardType.Heal: return <HealIcon className={cls} />
    case CardType.PsySearch: return <DrawIcon className={cls} />
    case CardType.Steal: return <StealIcon className={cls} />
    case CardType.Shock: return <ShockIcon className={cls} />
    case CardType.Energize: return <EnergizeIcon className={cls} />
    case CardType.Hibernation: return <HibernationIcon className={cls} />
    case CardType.Pushback: return <PushbackIcon className={cls} />
    case CardType.Dispel: return <DispelIcon className={cls} />
    case CardType.DoubleCross: return <DoubleCrossIcon className={cls} />
    case CardType.Counterblast: return <CounterblastIcon className={cls} />
    case CardType.Chromabow: return <ChromabowIcon className={cls} />
    case CardType.Disease: return <DiseaseIcon className={cls} />
    case CardType.DiscerningEye: return <DiscerningEyeIcon className={cls} />
    case CardType.BodyUpgrade: return <BodyUpgradeIcon className={cls} />
    case CardType.FortunesFavor: return <FortunesFavorIcon className={cls} />
    case CardType.BrokenSecret: return <BrokenSecretIcon className={cls} />
    case CardType.CursedIron: return <CursedIronIcon className={cls} />
    case CardType.Ale: return <AleIcon className={cls} />
    case CardType.EnergySword: return <EnergySwordIcon className={cls} />
    default: return <ShieldIcon className={cls} />
  }
}

export const DoubleCrossIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg xmlns="http://www.w3.org/2000/svg" className={className} viewBox="0 0 24 24" fill="currentColor">
    <g clipPath="url(#clip0_105_2)">
      <path d="M18.88 14.18C18.29 13.59 17.5 13 16 13s-2.29.59-2.88 1.18c-.2.19-.51.19-.71 0l-.71-.71a.5.5 0 00-.71 0C10.4 14.09 10 15.05 10 16c0 .34.03.68.08 1H4v-5h3V9H4V6h5.73C9.28 5.4 9 4.74 9 4c0-1.1.9-2 2-2s2 .9 2 2c0 .74-.28 1.4-.73 1.9L16 9.5V11h3v3.08c.32-.05.66-.08 1-.08.95 0 1.91.4 2.5 1 .49.49.49 1.28 0 1.77l-.71.71c-.2.2-.51.2-.71 0z" />
      <path d="M4.929 4.929l14.142 14.142" stroke="white" strokeWidth="2" strokeLinecap="round" />
      <path d="M19.071 4.929L4.929 19.071" stroke="white" strokeWidth="2" strokeLinecap="round" />
    </g>
    <defs>
      <clipPath id="clip0_105_2">
        <rect width="24" height="24" fill="white" />
      </clipPath>
    </defs>
  </svg>
);

export const ShockIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg xmlns="http://www.w3.org/2000/svg" className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M14 18v-2h2v2h-2zm-6-2v2H6v-2h2zm10-1c1.3 0 2.4.83 2.82 2H22v-2h-2v-2h-2v2h-1.18c-.42-1.17-1.52-2-2.82-2s-2.4.83-2.82 2H10v-2H8v2H6v2h1.18c.42-1.17 1.52-2 2.82-2s2.4.83 2.82 2H14v-2h-2v-2h2v-2H8.82c-.42 1.17-1.52 2-2.82 2S3.6 8.17 3.18 7H2v2h2v2h2V9h1.18c.42 1.17 1.52 2 2.82 2s2.4-.83 2.82-2H16v2h2v2h-2v2h3.18c.42-1.17 1.52-2 2.82-2zM4 6h2V4H4v2zm14-2v2h2V4h-2z" />
  </svg>
);

export const EnergizeIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg xmlns="http://www.w3.org/2000/svg" className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M7 2v11h3v9l7-12h-4l4-8z" />
  </svg>
);

export const HibernationIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg xmlns="http://www.w3.org/2000/svg" className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 2L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-3zm0 4.5l1.54 3.11 3.46.5-2.5 2.44.59 3.45-3.09-1.62-3.09 1.62.59-3.45-2.5-2.44 3.46.5L12 6.5z" />
  </svg>
);

export const StashIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg xmlns="http://www.w3.org/2000/svg" className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z" />
  </svg>
);

export const ManualRotateIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg xmlns="http://www.w3.org/2000/svg" className={className} viewBox="0 0 24 24" fill="none">
    <defs>
      <linearGradient id="magicGradient" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#22d3ee" /> {/* Cyan */}
        <stop offset="50%" stopColor="#ffffff" /> {/* White hot center */}
        <stop offset="100%" stopColor="#a855f7" /> {/* Purple */}
      </linearGradient>
      <filter id="glowFilter">
        <feGaussianBlur stdDeviation="0.5" result="coloredBlur" />
        <feMerge>
          <feMergeNode in="coloredBlur" />
          <feMergeNode in="SourceGraphic" />
        </feMerge>
      </filter>
    </defs>

    {/* Layer 1: The Ancient Runic Circle (Background, rotates slowly) */}
    <g className="rotate-icon-runes" opacity="0.4">
      {/* Dashed outer ring resembling runes */}
      <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="0.5" strokeDasharray="1 3" strokeLinecap="round" />
      <circle cx="12" cy="12" r="8" stroke="currentColor" strokeWidth="0.2" strokeDasharray="4 4" />
    </g>

    {/* Layer 2: The Core Energy Swirls (The clickable "action" part) */}
    <g className="rotate-icon-swirls" filter="url(#glowFilter)">
      {/* Swirl 1 */}
      <path
        d="M12 2C8 2 4.5 4.5 3 8l-1.5-1v5h5L4.8 10.2c1.2-2.5 3.8-4.2 6.7-4.2 2.5 0 4.7 1.2 6.1 3"
        stroke="url(#magicGradient)"
        strokeWidth="1.5"
        strokeLinecap="round"
        fill="none"
      />
      {/* Swirl 2 (inverted) */}
      <path
        d="M12 22c4 0 7.5-2.5 9-6l1.5 1v-5h-5l1.7 1.8c-1.2 2.5-3.8 4.2-6.7 4.2-2.5 0-4.7-1.2-6.1-3"
        stroke="url(#magicGradient)"
        strokeWidth="1.5"
        strokeLinecap="round"
        fill="none"
      />
    </g>
  </svg>
);

export const DispelIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg xmlns="http://www.w3.org/2000/svg" className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm-5-9h10v2H7v-2z" transform="rotate(45 12 12)" />
    <path d="M3.27 8.5L5.09 7.09l1.41 1.41L4.68 10.32zM7.09 5.09L8.5 3.27l1.81 1.81-1.41 1.41zM12 4c.55 0 1-.45 1-1s-.45-1-1-1-1 .45-1 1 .45 1 1 1zm5.91 1.09l-1.41 1.41 1.81 1.81 1.41-1.41zM20.73 8.5l-1.81 1.81 1.41 1.41 1.81-1.81zM12 20c-.55 0-1 .45-1 1s.45 1 1 1 1-.45 1-1-.45-1-1-1zm-4.91-1.09l1.41-1.41-1.81-1.81-1.41 1.41z" />
  </svg>
);

export const BodyUpgradeIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg xmlns="http://www.w3.org/2000/svg" className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" />
    <path d="M11 15h2v-4h4V9h-4V5h-2v4H7v2h4z" fill="white" />
    <path d="M12 1.5l-2.5 5h5z" fill="white" />
  </svg>
);

export const FortunesFavorIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg xmlns="http://www.w3.org/2000/svg" className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z" />
    <path d="M11 13H5v-2h6V5h2v6h6v2h-6v6h-2z" fill="white" transform="scale(0.6) translate(8, 8)" />
  </svg>
);

export const BrokenSecretIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg xmlns="http://www.w3.org/2000/svg" className={className} viewBox="0 0 24 24" fill="currentColor">
    <g>
      <path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z" />
      <path d="M3 3l18 18" stroke="#111827" strokeWidth="2.5" strokeLinecap="round" />
      <path d="M4 4l16 16" stroke="white" strokeWidth="1.5" strokeLinecap="round" />
    </g>
  </svg>
);

export const CursedIronIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg xmlns="http://www.w3.org/2000/svg" className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 2L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-3z" />
    <path d="M11.5 6l-1 5 3.5-2 -1 5 4-7.5h-4l1.5-5.5z" stroke="white" strokeWidth="1.2" fill="none" transform="rotate(15 12 12)" />
  </svg>
);

export const CounterblastIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg xmlns="http://www.w3.org/2000/svg" className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 2L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-3z" />
    <path d="M15.54 11.54L12 8l-3.54 3.54 1.41 1.41L12 10.83l2.12 2.12 1.42-1.41zM12 16a4 4 0 0 0 4-4h-2a2 2 0 0 1-2 2 2 2 0 0 1-2-2H8a4 4 0 0 0 4 4z" fill="white" />
  </svg>
);

export const ChromabowIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg xmlns="http://www.w3.org/2000/svg" className={className} viewBox="0 0 24 24" fill="none" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <defs>
      <linearGradient id="chroma-gradient" x1="0%" y1="0%" x2="0%" y2="100%">
        <stop offset="0%" stopColor="#ef4444" />
        <stop offset="20%" stopColor="#eab308" />
        <stop offset="40%" stopColor="#22c55e" />
        <stop offset="60%" stopColor="#3b82f6" />
        <stop offset="80%" stopColor="#a855f7" />
        <stop offset="100%" stopColor="#ec4899" />
      </linearGradient>
    </defs>
    <path d="M12 22C6.477 22 2 17.523 2 12S6.477 2 12 2" stroke="url(#chroma-gradient)" strokeWidth="2.5" />
    <path d="M2 12h20" stroke="white" strokeWidth="1.5" />
    <path d="M18 9l3 3-3 3" stroke="white" strokeWidth="1.5" />
  </svg>
);

export const DiseaseIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg xmlns="http://www.w3.org/2000/svg" className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8z" opacity=".3" />
    <path d="M16.5 8c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zM7.5 8c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zM12 14c-2.33 0-4.31 1.46-5.11 3.5h10.22c-.8-2.04-2.78-3.5-5.11-3.5z" />
  </svg>
);

export const DiscerningEyeIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg xmlns="http://www.w3.org/2000/svg" className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z" />
    <circle cx="12" cy="12" r="2" fill="white" />
    <path d="M12 7.5L15 11.5L12 15.5L9 11.5z" fill="white" opacity="0.6" />
  </svg>
);

export const AleIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg xmlns="http://www.w3.org/2000/svg" className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M20 19.95V10c0-1.1-.9-2-2-2H7c-1.1 0-2 .9-2 2v9.95c0 .58.47 1.05 1.05 1.05h12.9c.58 0 1.05-.47 1.05-1.05zM7 10h10v9H7v-9z" />
    <path d="M17 5v3h-2V5c0-1.1-.9-2-2-2h-2c-1.1 0-2 .9-2 2v3H7V5c0-2.21 1.79-4 4-4h2c2.21 0 4 1.79 4 4z" />
    <path d="M10 11h4v6h-4z" fill="white" opacity="0.2" />
  </svg>
);

export const EnergySwordIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg xmlns="http://www.w3.org/2000/svg" className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M14.803 2.003l-1.414 1.414L15.586 5.58l-1.47 1.47-1.47-1.47-1.414 1.414 1.47 1.47-1.47 1.47-1.414 1.414 1.47 1.47-1.47 1.47L9.5 17H5v-4.5l6.536-6.536 1.414-1.414 1.353-1.354zm-2.828 2.828l3.535 3.535-1.414 1.414-3.535-3.535 1.414-1.414zM2 18h11v2H2v-2z" />
    <path d="M12 2L10 9h4L12 2zm-2 9l-2 7h8l-2-7H10z" fill="cyan" opacity="0.6" />
  </svg>
);

export const EyesOfMadnessIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg xmlns="http://www.w3.org/2000/svg" className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5z" />
    <circle cx="12" cy="12" r="3" fill="white" opacity="0.8" />
    <path d="M12 12m-5 0a5 5 0 1 0 10 0a5 5 0 1 0-10 0" stroke="white" strokeWidth="0.5" strokeDasharray="2 2" fill="none">
      <animateTransform attributeName="transform" type="rotate" from="0 12 12" to="360 12 12" dur="5s" repeatCount="indefinite" />
    </path>
    <path d="M12 12m-1 0a1 1 0 1 0 2 0a1 1 0 1 0-2 0" fill="red" />
  </svg>
);

export const MadnessControlIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg xmlns="http://www.w3.org/2000/svg" className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5z" />
    <circle cx="12" cy="12" r="3" fill="white" opacity="0.8" />
    <path d="M12 12m-5 0a5 5 0 1 0 10 0a5 5 0 1 0-10 0" stroke="white" strokeWidth="0.5" strokeDasharray="2 2" fill="none">
      <animateTransform attributeName="transform" type="rotate" from="0 12 12" to="360 12 12" dur="5s" repeatCount="indefinite" />
    </path>
    <path d="M12 12m-1 0a1 1 0 1 0 2 0a1 1 0 1 0-2 0" fill="red" />
    <circle cx="12" cy="12" r="8" fill="none" stroke="currentColor" strokeWidth="0.75" opacity="0.5" />
    <circle cx="12" cy="12" r="10" fill="none" stroke="currentColor" strokeWidth="0.5" opacity="0.3" />
  </svg>
);

export const FlyingScytheIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg xmlns="http://www.w3.org/2000/svg" className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M19.38 3.32a1.38 1.38 0 00-1.95 0l-9.8 9.8c-.37.38-.6.89-.6 1.43v2.83c0 .54.44 1 .99 1h2.83c.54 0 1.05-.23 1.42-.6l9.8-9.8a1.38 1.38 0 000-1.95l-2.72-2.74zM16.5 6.22l1.28 1.28-7.92 7.92H8.5v-1.36l7.92-7.92.08-.08z" />
    <path d="M3.75 14.25a.75.75 0 00-.75.75v5c0 .41.34.75.75.75h5a.75.75 0 000-1.5H5.25v-3.5a.75.75 0 00-.75-.75z" opacity="0.6" />
    <path d="M4 11.75a.75.75 0 000 1.5h3.5a.75.75 0 000-1.5H4z" opacity="0.6" />
  </svg>
);

export const FrostIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg xmlns="http://www.w3.org/2000/svg" className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M19.07 4.93l-1.41 1.41-1.42-1.42-1.41 1.41 1.42 1.42-1.41 1.41 1.41 1.41 1.42-1.42 1.41-1.41-1.42-1.42 1.41-1.41zM4.93 19.07l1.41-1.41 1.42 1.42 1.41-1.41-1.42-1.42 1.41-1.41-1.41-1.41-1.42 1.42-1.41 1.41 1.42 1.42-1.41 1.41z" />
    <path d="M12 2.5l-2 2-1.5-1.5-2 2 1.5 1.5-2 2 2 2 1.5-1.5 2 2-1.5-1.5 2-2 2-2-1.5-1.5-2-2 1.5-1.5-2-2zM12 15a3 3 0 110-6 3 3 0 010 6z" />
  </svg>
);

export const InfernoGauntletIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg xmlns="http://www.w3.org/2000/svg" className={className} viewBox="0 0 24 24" fill="currentColor">
    <g>
      {/* Gauntlet base */}
      <path d="M16 2l-4 4-4-4-2 2 3 3-3 3 2 2 3-3 3 3 2 2-3-3 3-3z" opacity="0.6" />
      {/* Flames */}
      <path d="M12 10c-2 0-3 1.5-3 3 0 2 1 3 3 3s3-1 3-3c0-1.5-1-3-3-3zm0 4.5c-.8 0-1.5-.7-1.5-1.5s.7-1.5 1.5-1.5 1.5.7 1.5 1.5-.7 1.5-1.5 1.5z" />
      <path d="M8 16c0 2.2 1.8 4 4 4s4-1.8 4-4" stroke="currentColor" strokeWidth="1.5" fill="none" />
      {/* Flame particles */}
      <circle cx="7" cy="14" r="1" />
      <circle cx="17" cy="14" r="1" />
      <circle cx="12" cy="8" r="1" />
    </g>
  </svg>
);

export const InvincibleIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg xmlns="http://www.w3.org/2000/svg" className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 2L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-3z" />
    <g fill="white">
      <path d="M12 10.2l1.2 2.5 2.8.4-2 1.9.5 2.8L12 16.4l-2.5 1.4.5-2.8-2-1.9 2.8-.4z" />
      <path d="M12 6l.9 1.8L15 8.7l-1.6 1.6.4 2.2L12 11.6l-1.8.9.4-2.2L9 8.7l2.1-.9z" opacity="0.7" />
    </g>
  </svg>
);

export const LarcendaggerIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg xmlns="http://www.w3.org/2000/svg" className={className} viewBox="0 0 24 24" fill="currentColor">
    <g>
      {/* Dagger blade */}
      <path d="M12 2l-2 8 2 2 2-2-2-8z" />
      <path d="M10 10l-3 3 2 2 3-3-2-2z" opacity="0.7" />
      {/* Handle/Guard */}
      <rect x="10" y="13" width="4" height="2" rx="1" />
      <path d="M11 15h2v3c0 .5-.5 1-1 1s-1-.5-1-1v-3z" />
      {/* Stealing effect - hand reaching */}
      <path d="M16 16c1 1 2 2 3 3l1-1c-1-1-2-2-3-3" stroke="currentColor" strokeWidth="1" fill="none" opacity="0.8" />
      <circle cx="19" cy="18" r="1.5" />
      {/* Shadow/motion lines */}
      <path d="M8 12l-2 2m3-1l-2 2m3-1l-2 2" stroke="currentColor" strokeWidth="0.5" opacity="0.5" />
    </g>
  </svg>
);

export const LifeChargerIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg xmlns="http://www.w3.org/2000/svg" className={className} viewBox="0 0 24 24" fill="currentColor">
    <g>
      {/* Heart base */}
      <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" />
      {/* Lightning bolt overlay */}
      <path d="M12 6l-2 6h3l-2 6 5-7h-3l3-5z" fill="white" opacity="0.9" />
      {/* Energy rings */}
      <circle cx="12" cy="12" r="8" fill="none" stroke="white" strokeWidth="0.5" opacity="0.4" />
      <circle cx="12" cy="12" r="10" fill="none" stroke="white" strokeWidth="0.3" opacity="0.3" />
    </g>
  </svg>
);

export const LifeDrainIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg xmlns="http://www.w3.org/2000/svg" className={className} viewBox="0 0 24 24" fill="currentColor">
    <g>
      {/* Draining heart (broken/dark side) */}
      <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09" opacity="0.4" />
      {/* Restoring heart (bright side) */}
      <path d="M12 5.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35" />
      {/* Energy transfer arrows */}
      <path d="M8 10l-2 2 2 2" stroke="currentColor" strokeWidth="1" fill="none" />
      <path d="M16 10l2 2-2 2" stroke="currentColor" strokeWidth="1" fill="none" />
      {/* Energy flow line */}
      <line x1="10" y1="12" x2="14" y2="12" stroke="white" strokeWidth="1.5" opacity="0.8" />
      {/* Plus symbol on right heart */}
      <path d="M17 11h2m-1-1v2" stroke="white" strokeWidth="1" opacity="0.9" />
      {/* Minus symbol on left heart */}
      <path d="M6 11h2" stroke="white" strokeWidth="1" opacity="0.9" />
    </g>
  </svg>
);

export const NaturalGripIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg xmlns="http://www.w3.org/2000/svg" className={className} viewBox="0 0 24 24" fill="currentColor">
    <g>
      {/* Base disease circle */}
      <circle cx="12" cy="12" r="9" opacity="0.3" />
      {/* Vine/root tendrils wrapping around */}
      <path d="M6 8 Q8 6, 10 8 T14 8 Q16 6, 18 8" stroke="currentColor" strokeWidth="1.5" fill="none" opacity="0.8" />
      <path d="M6 12 Q8 10, 10 12 T14 12 Q16 10, 18 12" stroke="currentColor" strokeWidth="1.5" fill="none" opacity="0.8" />
      <path d="M6 16 Q8 14, 10 16 T14 16 Q16 14, 18 16" stroke="currentColor" strokeWidth="1.5" fill="none" opacity="0.8" />
      {/* Grip/hand symbol in center */}
      <path d="M10 9v3m1-3v4m1-4v4m1-4v3" stroke="white" strokeWidth="1" />
      <path d="M9.5 12h4c.5 0 1 .5 1 1v1c0 .5-.5 1-1 1h-4c-.5 0-1-.5-1-1v-1c0-.5.5-1 1-1z" fill="white" opacity="0.7" />
      {/* Disease particles */}
      <circle cx="7" cy="7" r="1" opacity="0.6" />
      <circle cx="17" cy="7" r="1" opacity="0.6" />
      <circle cx="7" cy="17" r="1" opacity="0.6" />
      <circle cx="17" cy="17" r="1" opacity="0.6" />
    </g>
  </svg>
);

export const PlantedBombIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg xmlns="http://www.w3.org/2000/svg" className={className} viewBox="0 0 24 24" fill="currentColor">
    <g>
      <circle cx="12" cy="14" r="7" opacity="0.8" />
      <path d="M12 7L14 3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      <circle cx="14" cy="3" r="1.5" fill="orange" />
      <path d="M13 2l2 2m-2 0l2-2" stroke="orange" strokeWidth="1" />
      <path d="M12 14l-8-8m8 8l8-8m-8 8l-8 8m8-8l8 8" stroke="white" strokeWidth="0.5" opacity="0.6" />
      <path d="M12 11v3m0 2v1" stroke="white" strokeWidth="1.5" strokeLinecap="round" />
    </g>
  </svg>
);

export const PaladinsHammerIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg xmlns="http://www.w3.org/2000/svg" className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M13.26 3.6L5.05 11.82c-.8.8-.8 2.09 0 2.89l2.82 2.82c.8.8 2.09.8 2.89 0l8.22-8.22c.18-.18.29-.41.34-.65l1.64-7.98c.12-.59-.34-1.16-1.02-1.02l-7.98 1.64c-.24.05-.47.16-.65.34z M9.01 16.29c-.49.49-1.28.49-1.77 0l-2.82-2.82c-.49-.49-.49-1.28 0-1.77L14.49 1.63l4.59 4.59-10.07 10.07z" />
    <path d="M3.01 18.01l4.24 4.24c.49.49 1.28.49 1.77 0l4.24-4.24-4.24-4.24c-.49-.49-1.28-.49-1.77 0l-4.24 4.24z" />
  </svg>
);

export const ProtectorIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg xmlns="http://www.w3.org/2000/svg" className={className} viewBox="0 0 24 24" fill="currentColor">
    <g>
      <path d="M12 2L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-3z" />
      <path d="M12 6v12M8 12h8" stroke="white" strokeWidth="2" strokeLinecap="round" />
      <path d="M12 16l-2-2c-1-1-1-2 0-3 1-1 2-1 3 0 1-1 2-1 3 0 1 1 1 2 0 3l-2 2-2-2z" fill="#ff6b6b" opacity="0.8" transform="translate(0, 2) scale(0.8)" />
    </g>
  </svg>
);

export const PlayerInfo: React.FC<{ player: Player; language: Language; isOpponent?: boolean, onAcquireTowerClick?: () => void; onOwnedTowerClick?: (index: number) => void; canAcquireTower: boolean; hint?: { isAIBest: boolean } }> = ({ player, language, isOpponent = false, onAcquireTowerClick, onOwnedTowerClick, canAcquireTower, hint }) => {
  const { isMobileShell } = useShellLayout();
  const maxTowers = 5;
  const towerSlotClass = isMobileShell ? 'w-11 h-11' : 'w-14 h-14';
  const towerCrystalClass = isMobileShell ? 'w-9 h-9' : 'w-12 h-12';
  const towerSlots = [];
  for (let i = 0; i < maxTowers; i++) {
    if (player.towers[i]) {
      const tower = player.towers[i];
      const isClickable = !isOpponent && onOwnedTowerClick;
      towerSlots.push(
        <div
          key={`tower-${i}`}
          id={`tower-${player.id}-${tower.color}`}
          data-tower-color={tower.color}
          data-player-id={player.id}
          className={`${towerSlotClass} flex items-center justify-center ${isClickable ? 'cursor-pointer hover:scale-110 transition-transform' : ''}`}
          onClick={isClickable ? () => onOwnedTowerClick(i) : undefined}
        >
          <TowerCrystal color={tower.color} className={towerCrystalClass} />
        </div>
      );
    } else {
      const isClickable = !isOpponent && onAcquireTowerClick && canAcquireTower;
      towerSlots.push(
        <div
          key={`empty-${i}`}
          id={`empty-tower-${player.id}-${i}`}
          data-empty-tower-slot="true"
          data-player-id={player.id}
          className={`relative ${towerSlotClass} flex items-center justify-center ${isClickable ? 'cursor-pointer hover:scale-110 transition-transform' : 'opacity-40'}`}
          onClick={isClickable ? onAcquireTowerClick : undefined}
        >
          {isClickable && hint && (
            <ElementWaveSignal
              isAIBest={hint.isAIBest}
              elementType="BUTTON"
              shape="CIRCLE"
            />
          )}
          <TowerCrystal color="gray" className={towerCrystalClass} />
        </div>
      );
    }
  }

  return (
    <div className={`${isMobileShell ? 'w-60 gap-1.5 py-1' : 'w-80 gap-3 py-2'} flex flex-col items-center text-white ${isOpponent ? 'scale-y-[-1]' : ''}`}>
      <div className={`flex items-center justify-center ${isMobileShell ? 'gap-2' : 'gap-4'}`}>
        <div className={`flex items-center text-white ${isMobileShell ? 'gap-1.5' : 'gap-2'}`} style={{ transform: isOpponent ? 'scaleY(-1)' : 'none' }}>
          <span className={`${isMobileShell ? 'text-base w-5 h-5' : 'text-lg w-6 h-6'} font-bold flex items-center justify-center`}>
            <AnimatedCounter value={player.hand.length} fontSize={isMobileShell ? 16 : 18} fontWeight="bold" />
          </span>
          <span className={`${isMobileShell ? 'text-xs' : 'text-sm'} font-semibold`}>{t('ui.hand', language)}</span>
        </div>
      </div>
      <div className={`flex items-center justify-center w-full ${isMobileShell ? 'gap-0.5 px-1' : 'gap-1 px-2'}`} style={{ transform: isOpponent ? 'scaleY(-1)' : 'none' }}>
        {towerSlots}
      </div>
    </div>
  );
};

export const getLogIcon = (log: string): React.ReactNode => {
  const lowerLog = log.toLowerCase();
  // Attack / Damage
  if (lowerLog.includes('damage') || lowerLog.includes('attack') || lowerLog.includes('chromabow') ||
    lowerLog.includes('урон') || lowerLog.includes('атак') || lowerLog.includes('伤害') || lowerLog.includes('攻击') ||
    lowerLog.includes('log.bodyupgradelog') || lowerLog.includes('log.chromabowdamagelog') || lowerLog.includes('log.protectorattacklog')) {
    return <AttackIcon className="w-4 h-4 text-red-400 flex-shrink-0" />;
  }
  // Natural Grip
  if (lowerLog.includes('natural grip') || lowerLog.includes('природн') || lowerLog.includes('自然')) {
    return <NaturalGripIcon className="w-4 h-4 text-lime-400 flex-shrink-0" />;
  }
  // Disease
  if (lowerLog.includes('disease') || lowerLog.includes('болезн') || lowerLog.includes('疾病')) {
    return <DiseaseIcon className="w-4 h-4 text-lime-400 flex-shrink-0" />;
  }
  // Heal / Health / Upgrade
  if (lowerLog.includes('heals') || lowerLog.includes('upgrades body') || lowerLog.includes('restores') ||
    lowerLog.includes('лечит') || lowerLog.includes('восстанав') || lowerLog.includes('治疗') || lowerLog.includes('恢复') || lowerLog.includes('升级') ||
    lowerLog.includes('log.bodyupgradelog') || lowerLog.includes('log.protectorrestorelog')) {
    return <BodyUpgradeIcon className="w-4 h-4 text-pink-400 flex-shrink-0" />;
  }
  // Essence
  if (lowerLog.includes('essence') || lowerLog.includes('эссенц') || lowerLog.includes('精华') || lowerLog.includes('艾森斯')) {
    return <EssenceIcon className="w-4 h-4 text-cyan-400 flex-shrink-0" />;
  }
  // Tower
  if (lowerLog.includes('tower') || lowerLog.includes('башн') || lowerLog.includes('塔')) {
    return <TowerIcon className="w-4 h-4 text-yellow-400 flex-shrink-0" />;
  }
  // Draw / Fortune
  if (lowerLog.includes('draw') || lowerLog.includes('fortune') || lowerLog.includes('возьмите') || lowerLog.includes('удач') || lowerLog.includes('抽') || lowerLog.includes('幸运') ||
    lowerLog.includes('log.fortunesfavorlog') || lowerLog.includes('log.chromabowdrawlog')) {
    return <FortunesFavorIcon className="w-4 h-4 text-yellow-400 flex-shrink-0" />;
  }
  // Steal
  if (lowerLog.includes('steals') || lowerLog.includes('укра') || lowerLog.includes('偷') || lowerLog.includes('log.stolencardslog')) {
    return <StealIcon className="w-4 h-4 text-purple-400 flex-shrink-0" />;
  }
  // Pushback / Return
  if (lowerLog.includes('returns') || lowerLog.includes('pushback') || lowerLog.includes('возвращ') || lowerLog.includes('推回') ||
    lowerLog.includes('log.slotoverflowlog') || lowerLog.includes('log.lockedreturnslot') || lowerLog.includes('log.effectexpirereturn') || lowerLog.includes('log.lockedreturn')) {
    return <PushbackIcon className="w-4 h-4 text-slate-400 flex-shrink-0" />;
  }
  // Dispel / Banish
  if (lowerLog.includes('dispel') || lowerLog.includes('banishes') || lowerLog.includes('рассеив') || lowerLog.includes('驱散')) {
    return <DispelIcon className="w-4 h-4 text-cyan-400 flex-shrink-0" />;
  }
  // Double Cross / Eliminate
  if (lowerLog.includes('double-cross') || lowerLog.includes('eliminates') || lowerLog.includes('подстава') || lowerLog.includes('双重背叛')) {
    return <DoubleCrossIcon className="w-4 h-4 text-purple-400 flex-shrink-0" />;
  }
  // Psy-block / Lock
  if (lowerLog.includes('psy-block') || lowerLog.includes('locks') || lowerLog.includes('пси-блок') || lowerLog.includes('封锁') ||
    lowerLog.includes('log.chooselocked')) {
    return <PsyBlockIcon className="w-4 h-4 text-slate-400 flex-shrink-0" />;
  }
  // Broken Secret / Reveal
  if (lowerLog.includes('broken secret') || lowerLog.includes('reveals') || lowerLog.includes('секрет') || lowerLog.includes('秘密') || lowerLog.includes('раскры') ||
    lowerLog.includes('log.brokensecretlog')) {
    return <BrokenSecretIcon className="w-4 h-4 text-cyan-400 flex-shrink-0" />;
  }
  // Counterblast
  if (lowerLog.includes('counterblast') || lowerLog.includes('контрудар') || lowerLog.includes('绝地反击') ||
    lowerLog.includes('log.counterblastreflect')) {
    return <CounterblastIcon className="w-4 h-4 text-cyan-400 flex-shrink-0" />;
  }
  // Cursed Iron
  if (lowerLog.includes('cursed iron') || lowerLog.includes('желез') || lowerLog.includes('铁')) {
    return <CursedIronIcon className="w-4 h-4 text-yellow-400 flex-shrink-0" />;
  }
  // Time markers
  if (lowerLog.includes('--- night') || lowerLog.includes('--- ночь') || lowerLog.includes('--- 黑夜')) {
    return <span className="text-indigo-400">🌙</span>;
  }
  if (lowerLog.includes('prepare for battle') || lowerLog.includes('готовьтесь к битве') || lowerLog.includes('准备战斗')) {
    return <span className="text-orange-400">☀️</span>;
  }

  return <div className="w-4 h-4 flex items-center justify-center text-slate-500 flex-shrink-0">•</div>;
};

export const GameLog: React.FC<{ logs: string[], language?: Language }> = ({ logs, language = 'en' }) => {
  const logContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (logContainerRef.current) {
      logContainerRef.current.scrollTop = logContainerRef.current.scrollHeight;
    }
  }, [logs]);

  return (
    <div className="w-64">
      <h3 className="font-bold text-white text-lg w-full text-center bg-black/30 p-2 rounded-lg group-hover:rounded-b-none backdrop-blur-sm cursor-default transition-all duration-500">{t('ui.battleLog', language)}</h3>
      <div className="max-h-0 overflow-hidden group-hover:max-h-[22rem] transition-all duration-500 ease-in-out">
        <div ref={logContainerRef} className="flex flex-col gap-2 overflow-y-auto w-full pr-1 p-2 bg-black/30 rounded-b-lg h-[22rem]">
          {logs.map((log, index) => (
            <div key={index} className="flex items-start gap-2 animate-[fadeIn_0.5s_ease-out]">
              {getLogIcon(log)}
              <p className="text-sm text-slate-300 leading-tight flex-1">{parseLog(log, language)}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export const MenuIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg xmlns="http://www.w3.org/2000/svg" className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M3 6h18v2H3V6zm0 5h18v2H3v-2zm0 5h18v2H3v-2z" />
  </svg>
);
